import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import db, { auth } from '../firebase';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  addDoc,
} from 'firebase/firestore';
import './APage.css';

const APage = () => {
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [interns, setInterns] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    joiningDate: '',
    endingDate: '',
    domain: '',
    responsibilities: '',
    projects: '',
  });

  const [certFormData, setCertFormData] = useState({
    name: '',
    certificateId: '',
    grade: '',
    courseName: '',
    startDate: '',
    endDate: '',
    description: '',
    feePending: false,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const bookingsSnapshot = await getDocs(collection(db, 'bookings'));
      setBookings(bookingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const contactsSnapshot = await getDocs(collection(db, 'contacts'));
      setContacts(contactsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const internsSnapshot = await getDocs(collection(db, 'Interns'));
      setInterns(internsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const certificatesSnapshot = await getDocs(collection(db, 'certificates'));
      setCertificates(certificatesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Delete this booking?")) return;
    await deleteDoc(doc(db, 'bookings', bookingId));
    setBookings(prev => prev.filter(b => b.id !== bookingId));
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Delete this contact message?")) return;
    await deleteDoc(doc(db, 'contacts', contactId));
    setContacts(prev => prev.filter(c => c.id !== contactId));
  };

  const handleDeleteIntern = async (internId) => {
    if (!window.confirm("Delete this intern?")) return;
    await deleteDoc(doc(db, 'Interns', internId));
    setInterns(prev => prev.filter(i => i.id !== internId));
  };

  const handleDeleteCertificate = async (certificateId) => {
    if (!window.confirm("Delete this certificate?")) return;
    await deleteDoc(doc(db, 'certificates', certificateId));
    setCertificates(prev => prev.filter(c => c.id !== certificateId));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCertInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCertFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAddIntern = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'Interns'), {
        ...formData,
        responsibilities: formData.responsibilities.split(',').map(s => s.trim()),
        projects: formData.projects.split(',').map(s => s.trim()),
      });
      setFormData({
        name: '',
        joiningDate: '',
        endingDate: '',
        domain: '',
        responsibilities: '',
        projects: '',
      });
      fetchData();
    } catch (error) {
      console.error("Error adding intern:", error);
      alert("Failed to add intern.");
    }
  };

  const handleAddCertificate = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'certificates'), certFormData);
      setCertFormData({
        name: '',
        certificateId: '',
        grade: '',
        courseName: '',
        startDate: '',
        endDate: '',
        description: '',
        feePending: false,
      });
      fetchData();
    } catch (error) {
      console.error("Error adding certificate:", error);
      alert("Failed to add certificate.");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/alogin');
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>📊 Admin Dashboard</h1>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </div>

      {loading ? <p>Loading data...</p> : (
        <>
          {/* BOOKINGS */}
          <section>
            <h2>📝 Bookings</h2>
            {bookings.length === 0 ? <p>No bookings.</p> : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Email</th><th>Phone</th><th>Service</th>
                    <th>Budget</th><th>Contact Method</th><th>Timeline</th><th>Message</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map(b => (
                    <tr key={b.id}>
                      <td>{b.name}</td><td>{b.email}</td><td>{b.phone}</td><td>{b.service}</td>
                      <td>{b.budget}</td><td>{b.contactMethod}</td><td>{b.timeline}</td><td>{b.message}</td>
                      <td><button className="delete-btn" onClick={() => handleDeleteBooking(b.id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* CONTACTS */}
          <section>
            <h2>📬 Contact Submissions</h2>
            {contacts.length === 0 ? <p>No contact messages.</p> : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Email</th><th>Phone</th><th>Message</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(c => (
                    <tr key={c.id}>
                      <td>{c.name}</td><td>{c.email}</td><td>{c.phone || 'N/A'}</td><td>{c.message}</td>
                      <td><button className="delete-btn" onClick={() => handleDeleteContact(c.id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* INTERNS MANAGEMENT */}
          <section>
            <h2>👨‍💻 Manage Interns</h2>

            {/* Add Intern Form */}
            <form className="intern-form" onSubmit={handleAddIntern}>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Name" required />
              <input type="date" name="joiningDate" value={formData.joiningDate} onChange={handleInputChange} required />
              <input type="date" name="endingDate" value={formData.endingDate} onChange={handleInputChange} required />
              <input type="text" name="domain" value={formData.domain} onChange={handleInputChange} placeholder="Domain" required />
              <input type="text" name="responsibilities" value={formData.responsibilities} onChange={handleInputChange} placeholder="Responsibilities (comma-separated)" required />
              <input type="text" name="projects" value={formData.projects} onChange={handleInputChange} placeholder="Projects Done (comma-separated)" required />
              <button type="submit">Add Intern</button>
            </form>

            {/* Intern Display */}
            {interns.length === 0 ? <p>No interns added yet.</p> : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Join</th><th>End</th><th>Domain</th><th>Responsibilities</th><th>Projects</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {interns.map(i => (
                    <tr key={i.id}>
                      <td>{i.name}</td>
                      <td>{i.joiningDate}</td>
                      <td>{i.endingDate}</td>
                      <td>{i.domain}</td>
                      <td>{Array.isArray(i.responsibilities) ? i.responsibilities.join(', ') : ''}</td>
                      <td>{Array.isArray(i.projects) ? i.projects.join(', ') : ''}</td>
                      <td>
                        <button className="delete-btn" onClick={() => handleDeleteIntern(i.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* CERTIFICATES MANAGEMENT */}
          <section>
            <h2>📜 Manage Certificates</h2>

            {/* Add Certificate Form */}
            <form className="certificate-form" onSubmit={handleAddCertificate}>
              <input type="text" name="name" value={certFormData.name} onChange={handleCertInputChange} placeholder="Name of Candidate" required />
              <input type="text" name="certificateId" value={certFormData.certificateId} onChange={handleCertInputChange} placeholder="Certificate ID" required />
              <input type="text" name="grade" value={certFormData.grade} onChange={handleCertInputChange} placeholder="Grade" required />
              <input type="text" name="courseName" value={certFormData.courseName} onChange={handleCertInputChange} placeholder="Course Name" required />
              <input type="date" name="startDate" value={certFormData.startDate} onChange={handleCertInputChange} required />
              <input type="date" name="endDate" value={certFormData.endDate} onChange={handleCertInputChange} required />
              <textarea name="description" value={certFormData.description} onChange={handleCertInputChange} placeholder="Description" required />
              <label>
                <input type="checkbox" name="feePending" checked={certFormData.feePending} onChange={handleCertInputChange} />
                Fee Pending
              </label>
              <button type="submit">Add Certificate</button>
            </form>

            {/* Certificate Display */}
            {certificates.length === 0 ? <p>No certificates added yet.</p> : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Certificate ID</th><th>Grade</th><th>Course Name</th><th>Start Date</th><th>End Date</th><th>Description</th><th>Fee Pending</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates.map(c => (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td>{c.certificateId}</td>
                      <td>{c.grade}</td>
                      <td>{c.courseName}</td>
                      <td>{c.startDate}</td>
                      <td>{c.endDate}</td>
                      <td>{c.description}</td>
                      <td>{c.feePending ? 'Yes' : 'No'}</td>
                      <td>
                        <button className="delete-btn" onClick={() => handleDeleteCertificate(c.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default APage;